import { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";

import Icon from "../Icon";
import StudentInfoDao from "../../dao/StudentInfoDao";

import { GlobalContext } from "../contextProvider/ContextProvider";

/**
 * Return a student's picture, or if it doesn't exist, return a default placeholder
 * @name StudentImage
 * @param {string} className
 * @param {string} height
 * @param {{}} studentInfo
 * @param {string} width
 * @return {JSX.Element}
 */
const StudentImage = ({ className, height, studentInfo, width }) => {
    const { dispatch, state } = useContext(GlobalContext);
    const { schoolYearDto, studentImageList, token } = state || {};

    const [studentImageUrl, setStudentImageUrl] = useState(null);

    useEffect(() => {
        if (studentImageList && studentInfo && !studentImageUrl) {
            const photoMatch = studentImageList.filter(
                (imgObj) => parseInt(imgObj.studentNumber, 10) === parseInt(studentInfo.studentNumber, 10)
            );
            if (photoMatch?.length > 0) {
                setStudentImageUrl(photoMatch[0]);
            }
        }
    }, [studentImageList, studentImageUrl, studentInfo]);

    /**
     * get the student's picture
     */
    useEffect(() => {
        if (studentImageUrl || !schoolYearDto || !studentInfo || !token) {
            return;
        }

        const fetchStudentImage = async () => {
            try {
                const options = {
                    action: "studentImageRead",
                    ignoreError: true,
                    schoolYearKey: schoolYearDto.key,
                    studentNumber: studentInfo.studentNumber,
                    token
                };
                const response = await StudentInfoDao(options);
                if (response && response.data) {
                    const urlCreator = window.URL || window.webkitURL;
                    const photoObj = {
                        studentNumber: studentInfo.studentNumber,
                        imageUrl: urlCreator.createObjectURL(new Blob([response.data], { type: "image/jpeg" }))
                    };
                    dispatch({
                        type: "StudentImageList",
                        studentImageList: [...(studentImageList || []), photoObj]
                    });
                    setStudentImageUrl(photoObj);
                }
            } catch (error) {
                console.error(
                    `Unable to retrieve student image for ${studentInfo.firstName} ${studentInfo.lastName}:\n`,
                    error
                );
            }
        };
        if (studentImageList) {
            const photoMatch = studentImageList.filter(
                (imgObj) => parseInt(imgObj.studentNumber, 10) === parseInt(studentInfo.studentNumber, 10)
            );
            if (photoMatch?.length > 0) {
                setStudentImageUrl(photoMatch[0]);
            } else {
                fetchStudentImage();
            }
        } else {
            fetchStudentImage();
        }
    }, [dispatch, studentInfo, schoolYearDto, studentImageList, studentImageUrl, token]);

    return (
        <div className="d-flex flex-row me-2">
            {studentImageUrl ? (
                <img
                    alt={`${studentInfo.firstName} ${studentInfo.lastName}`}
                    className={className}
                    height={`${height}px`}
                    src={studentImageUrl.imageUrl}
                />
            ) : (
                <Icon iconName="NO_STUDENT_IMAGE" fill="#19608F" height={height} width={width} />
            )}
        </div>
    );
};

StudentImage.propTypes = {
    className: PropTypes.string,
    height: PropTypes.number,
    studentInfo: PropTypes.oneOfType([PropTypes.object]).isRequired,
    width: PropTypes.number
};

StudentImage.defaultProps = {
    className: "",
    height: 80,
    width: 80
};

export default StudentImage;
